import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Calendar, Package, Plus, Minus, Loader2, MapPin, Navigation, Route } from 'lucide-react';
import VehicleSelector from './VehicleSelector';
import VehicleSearchInput from './VehicleSearchInput';
import AddressInput from './AddressInput';
import OrderSuccessModal from './OrderSuccessModal';
import MultiStopInput from './MultiStopInput';
import RoundTripSection from './operation/RoundTripSection';
import { vehicleTypes, operationTypes, merchandiseTypes, containerTypes, countries } from '../mockData';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { logger } from '../utils/logger';
import { useExternalReference } from '../hooks/useExternalReference';

const token = 'cookie-auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Date du jour (fuseau local) — valeur par défaut des dates de l'opération
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const API = `${BACKEND_URL}/api`;

const TransportForm = ({ 
  onSubmit, 
  onAddressChange,
  onStopsChange,
  // Props pour création par admin
  clientId = null,
  clientInfo = null,
  isAdminCreating = false,
  onSuccess = null,
  onCancel = null,
  // Props pour template
  initialData = null
}) => {
  const { config: extRefConfig } = useExternalReference(
    isAdminCreating ? (clientInfo?.companyName || '') : undefined
  );
  const extRefEditable = extRefConfig.clientCanEdit || isAdminCreating;
  const [extRefReminded, setExtRefReminded] = useState(false);
  const [formData, setFormData] = useState({
    departure: '',
    arrival: '',
    deliveryDate: todayIso(),
    vehicleType: '',
    vehicleTypeOther: '',
    operationType: '',
    operationTypeOther: '',
    merchandiseType: '',
    merchandiseTypeOther: '',
    containerType: '',
    weight: 1,
    numberOfTrucks: 1,
    needsInsurance: false,
    merchandiseValue: '',
    specialInstructions: '',
    externalRef: '',
    // Multi-stop delivery
    stops: [],
    // ✨ Aller-retour (23/04/2026)
    tripType: 'one_way',
    returnCargoMode: null,
    returnMerchandiseType: '',
    returnMerchandiseTypeOther: '',
    returnWeight: '',
    returnContainerType: '',
    returnDepartureDate: '',
    distance_km: null,  // mis à jour depuis le calcul automatique
  });

  const [showDepartureComplement, setShowDepartureComplement] = useState(false);
  const [showArrivalComplement, setShowArrivalComplement] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdOperation, setCreatedOperation] = useState(null);
  const [showContainerOptions, setShowContainerOptions] = useState(false);
  const [showMultiStopSection, setShowMultiStopSection] = useState(false);
  
  // État pour le calcul de distance
  const [distanceInfo, setDistanceInfo] = useState({
    distance_km: null,
    isCalculating: false,
    error: null
  });

  // Apply initialData from template when provided
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        departure: initialData.departureAddress || prev.departure,
        arrival: initialData.arrivalAddress || prev.arrival,
        departureCoordinates: initialData.departureCoordinates || prev.departureCoordinates,
        arrivalCoordinates: initialData.arrivalCoordinates || prev.arrivalCoordinates,
        vehicleType: initialData.vehicleType || prev.vehicleType,
        operationType: initialData.operationType || prev.operationType,
        merchandiseType: initialData.merchandiseType || prev.merchandiseType,
        containerType: initialData.containerType || prev.containerType,
        weight: initialData.weight || prev.weight,
        numberOfTrucks: initialData.numberOfTrucks || prev.numberOfTrucks,
        specialInstructions: initialData.notes || prev.specialInstructions,
      }));
      // Update container options visibility
      if (initialData.operationType) {
        const selectedOp = operationTypes.find(op => op.value === initialData.operationType);
        setShowContainerOptions(selectedOp?.showContainerOption || false);
      }
    }
  }, [initialData]);

  // Fonction pour calculer la distance entre deux points
  const calculateSegmentDistance = async (from, to) => {
    if (!from || !to || from.length < 3 || to.length < 3) {
      return null;
    }

    try {
      const response = await axios.get(`${API}/external/distance/estimate`, {
        params: { from_city: from, to_city: to }
      });
      return response.data?.distance_km || null;
    } catch (error) {
      logger.debug('Distance calculation error:', error.message);
      return null;
    }
  };

  // Fonction pour calculer la distance totale (avec étapes)
  const calculateTotalDistance = useCallback(async (departure, arrival, stops = []) => {
    if (!departure || !arrival || departure.length < 3 || arrival.length < 3) {
      setDistanceInfo({ distance_km: null, isCalculating: false, error: null });
      return;
    }

    setDistanceInfo(prev => ({ ...prev, isCalculating: true, error: null }));

    try {
      // Construire la liste des points dans l'ordre
      const waypoints = [departure];
      
      // Ajouter les étapes intermédiaires (triées par ordre)
      const sortedStops = [...stops].sort((a, b) => a.order - b.order);
      sortedStops.forEach(stop => {
        if (stop.address && stop.address.length >= 3) {
          waypoints.push(stop.address);
        }
      });
      
      waypoints.push(arrival);

      // Si pas d'étapes, calcul simple
      if (waypoints.length === 2) {
        const distance = await calculateSegmentDistance(departure, arrival);
        setDistanceInfo({
          distance_km: distance,
          isCalculating: false,
          error: null,
          segments: 1
        });
        return;
      }

      // Calculer la distance de chaque segment
      let totalDistance = 0;
      let segments = [];
      
      for (let i = 0; i < waypoints.length - 1; i++) {
        const segmentDistance = await calculateSegmentDistance(waypoints[i], waypoints[i + 1]);
        if (segmentDistance) {
          totalDistance += segmentDistance;
          segments.push({
            from: waypoints[i],
            to: waypoints[i + 1],
            distance: segmentDistance
          });
        }
      }

      setDistanceInfo({
        distance_km: totalDistance > 0 ? Math.round(totalDistance) : null,
        isCalculating: false,
        error: null,
        segments: segments.length,
        segmentDetails: segments
      });
    } catch (error) {
      logger.debug('Total distance calculation error:', error.message);
      setDistanceInfo({ distance_km: null, isCalculating: false, error: null });
    }
  }, []);

  // Debounced distance calculation when addresses or stops change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.departure && formData.arrival) {
        calculateTotalDistance(formData.departure, formData.arrival, formData.stops || []);
      }
    }, 1000); // Attendre 1s après la dernière modification

    return () => clearTimeout(timer);
  }, [formData.departure, formData.arrival, formData.stops || [], calculateTotalDistance]);

  // ✨ Synchroniser la distance calculée dans formData pour le diagramme A/R
  // PM Spec 03/06/2026 — Pour les aller-retour, on double la distance (retour = même itinéraire).
  useEffect(() => {
    if (distanceInfo.distance_km) {
      const multiplier = formData.tripType === 'round_trip' ? 2 : 1;
      setFormData(prev => ({ ...prev, distance_km: distanceInfo.distance_km * multiplier }));
    }
  }, [distanceInfo.distance_km, formData.tripType]);

  const handleChange = (field, value, coordinates = null) => {
    // Stocker les coordonnées si disponibles
    if (coordinates && (field === 'departure' || field === 'arrival')) {
      const coordField = field === 'departure' ? 'departureCoordinates' : 'arrivalCoordinates';
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        [coordField]: coordinates
      }));
      onAddressChange?.(field, value, coordinates);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      
      if (field === 'departure' || field === 'arrival') {
        onAddressChange?.(field, value);
      }
    }
    
    // Check if container transport is selected
    if (field === 'operationType') {
      const selectedOp = operationTypes.find(op => op.value === value);
      setShowContainerOptions(selectedOp?.showContainerOption || false);
      
      // Auto-select first container type if container transport is selected
      if (selectedOp?.showContainerOption && !formData.containerType) {
        setFormData(prev => ({ 
          ...prev, 
          containerType: containerTypes[0].value 
        }));
      } else if (!selectedOp?.showContainerOption) {
        // Reset container type if switching away from container transport
        setFormData(prev => ({ ...prev, containerType: '' }));
      }
    }
  };

  const handleWeightChange = (increment) => {
    setFormData(prev => ({
      ...prev,
      weight: Math.max(1, Math.min(220, prev.weight + increment))
    }));
  };

  const handleWeightInput = (value) => {
    // Permettre la saisie vide temporairement pour l'édition
    if (value === '') {
      setFormData(prev => ({ ...prev, weight: '' }));
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setFormData(prev => ({
        ...prev,
        weight: Math.max(1, Math.min(220, numValue))
      }));
    }
  };

  const handleWeightBlur = () => {
    // S'assurer qu'on a une valeur valide quand on quitte le champ
    if (formData.weight === '' || isNaN(formData.weight)) {
      setFormData(prev => ({ ...prev, weight: 1 }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.departure || !formData.arrival || !formData.deliveryDate) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    // LOT B — Référence externe : blocage si obligatoire, rappel unique si facultative
    if (extRefConfig.enabled && extRefEditable && !formData.externalRef.trim()) {
      if (extRefConfig.required) {
        toast({
          title: `${extRefConfig.label} requis`,
          description: `${extRefConfig.label} est requis pour vos opérations.`,
          variant: "destructive"
        });
        return;
      }
      if (!extRefReminded) {
        setExtRefReminded(true);
        toast({
          title: `Souhaitez-vous ajouter votre ${extRefConfig.label} ?`,
          description: "Cliquez à nouveau sur « Envoyer » pour continuer sans le renseigner."
        });
        return;
      }
    }

    if (!formData.vehicleType || !formData.operationType) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner le type de véhicule et l'opération",
        variant: "destructive"
      });
      return;
    }

    // Validate merchandise or container type based on operation
    if (showContainerOptions && !formData.containerType) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner le type de conteneur",
        variant: "destructive"
      });
      return;
    }

    if (!showContainerOptions && !formData.merchandiseType) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner le type de marchandise",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Vérifier que le token existe
      if (!token) {
        toast({
          title: "Non authentifié",
          description: "Veuillez vous reconnecter",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      logger.debug('Submitting transport request with data:', formData);

      // Extraire les champs aller-retour pour les ajouter conditionnellement
      // (éviter que ...formData propage `returnWeight: ''` quand tripType=one_way,
      //  ce qui casse la validation Pydantic Optional[float])
      const {
        returnCargoMode: _rcm,
        returnMerchandiseType: _rmt,
        returnMerchandiseTypeOther: _rmto,
        returnWeight: _rw,
        returnContainerType: _rct,
        returnDepartureDate: _rdd,
        ...formDataWithoutReturn
      } = formData;

      // Préparer les données avec conversion des types
      const submitData = {
        ...formDataWithoutReturn,
        weight: Number(formData.weight) || 1,
        numberOfTrucks: Number(formData.numberOfTrucks) || 1,
        merchandiseValue: formData.merchandiseValue ? Number(formData.merchandiseValue) : 0,
        needsInsurance: Boolean(formData.needsInsurance),
        // Multi-stop: formater les étapes pour l'API (simplifié - sans stopType)
        stops: (formData.stops || []).length > 0 ? (formData.stops || []).map((stop, index) => ({
          order: index + 1,
          address: stop.address,
          city: stop.city || '',
          country: stop.country || 'CI',
          coordinates: stop.coordinates || null,
          stopType: 'passage', // Valeur par défaut simplifiée,
          instructions: stop.instructions || null
        })) : null,
        // ✨ Aller-retour — propagation des champs UNIQUEMENT si round_trip
        tripType: formData.tripType || 'one_way',
        ...(extRefConfig.enabled ? { externalRef: (formData.externalRef || '').trim() } : {}),
        ...(formData.tripType === 'round_trip' ? {
          returnCargoMode: formData.returnCargoMode || 'same',
          returnMerchandiseType:
            formData.returnCargoMode === 'same' ? formData.merchandiseType
            : formData.returnCargoMode === 'different' ? (formData.returnMerchandiseType || null)
            : null,
          returnMerchandiseTypeOther: formData.returnMerchandiseTypeOther || null,
          returnWeight:
            formData.returnCargoMode === 'same' ? (Number(formData.weight) || 1)
            : formData.returnCargoMode === 'different' ? (parseFloat(formData.returnWeight) || null)
            : null,
          returnContainerType:
            formData.returnCargoMode === 'same' ? (formData.containerType || null)
            : formData.returnContainerType || null,
          returnDepartureDate: formData.returnDepartureDate || null,
        } : {})
      };
      
      let response;
      
      // Si création par admin pour un client
      if (isAdminCreating) {
        if (!clientId) {
          // Garde-fou : jamais de rattachement silencieux au compte du staff connecté
          toast({
            variant: 'destructive',
            title: 'Client non identifié',
            description: "L'opération n'a pas été créée : sélectionnez à nouveau le client avant de valider.",
          });
          setIsSubmitting(false);
          return;
        }
        response = await axios.post(`${API}/admin/operations/create-for-client`, {
          ...submitData,
          clientId: clientId,
          createdByAdmin: true
        }, {
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Content-Type': 'application/json'
          }
        });
      } else {
        // Création normale par le client lui-même
        response = await axios.post(`${API}/transport-requests`, submitData, {
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.status === 201 || response.status === 200) {
        // Store operation data for modal
        setCreatedOperation(response.data);

        // Analytics : track feature.used (Phase 3)
        try {
          const { trackFeature } = require('../utils/analytics');
          trackFeature('operation.create', {
            distance: response.data?.totalDistance || null,
            stopsCount: (response.data?.stops || []).length,
          });
        } catch (_) { /* never block */ }
        
        // Si création par admin, appeler onSuccess et ne pas afficher le modal habituel
        if (isAdminCreating && onSuccess) {
          onSuccess(response.data);
          return;
        }
        
        // Show success modal (pour création par client)
        setShowSuccessModal(true);
        
        // Reset form
        setFormData({
          departure: '',
          arrival: '',
          deliveryDate: todayIso(),
          vehicleType: '',
          vehicleTypeOther: '',
          operationType: '',
          operationTypeOther: '',
          merchandiseType: '',
          merchandiseTypeOther: '',
          containerType: '',
          weight: 1,
          numberOfTrucks: 1,
          needsInsurance: false,
          merchandiseValue: '',
          specialInstructions: ''
        });
        
        onSubmit?.(response.data);
      }
    } catch (error) {
      console.error('Error submitting transport request:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', error.response?.data);
      
      // Format error message
      let errorMessage = "Une erreur s'est produite lors de l'envoi de votre demande";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            const field = err.loc ? err.loc.join(' → ') : 'Champ';
            return `${field}: ${err.msg}`;
          }).join('\n');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object with msg
        else if (detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Route Details */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-[#1db584]" />
          <h3 className="font-semibold text-gray-900">Détails du trajet *</h3>
        </div>
        
        <div>
          <AddressInput
            value={formData.departure}
            onChange={(value, coords) => handleChange('departure', value, coords)}
            placeholder="Adresse de départ"
          />
          <button
            type="button"
            onClick={() => setShowDepartureComplement(!showDepartureComplement)}
            className="text-[#1db584] text-sm mt-2 hover:text-[#16a075] hover:underline flex items-center gap-1 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un complément
          </button>
          {showDepartureComplement && (
            <Input
              className="mt-2"
              placeholder="Complément d'adresse"
            />
          )}
        </div>

        {/* Section Multi-Stop Toggle */}
        <div className="border-t border-b border-dashed border-gray-200 py-4 my-2">
          <button
            type="button"
            onClick={() => setShowMultiStopSection(!showMultiStopSection)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#1db584] transition-colors w-full justify-center"
          >
            <Route className="w-4 h-4" />
            {showMultiStopSection ? 'Masquer les étapes intermédiaires' : 'Ajouter des étapes intermédiaires'}
            {(formData.stops || []).length > 0 && (
              <span className="ml-2 bg-[#1db584] text-white text-xs px-2 py-0.5 rounded-full">
                {(formData.stops || []).length}
              </span>
            )}
          </button>
          
          {showMultiStopSection && (
            <div className="mt-4">
              <MultiStopInput
                stops={formData.stops || []}
                onStopsChange={(stops) => {
                  setFormData(prev => ({ ...prev, stops }));
                  // Callback to parent for map update
                  if (onStopsChange) {
                    onStopsChange(stops);
                  }
                }}
                maxStops={10}
              />
            </div>
          )}
        </div>

        <div>
          <AddressInput
            value={formData.arrival}
            onChange={(value, coords) => handleChange('arrival', value, coords)}
            placeholder="Adresse d'arrivée"
          />
          <button
            type="button"
            onClick={() => setShowArrivalComplement(!showArrivalComplement)}
            className="text-[#1db584] text-sm mt-2 hover:text-[#16a075] hover:underline flex items-center gap-1 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un complément
          </button>
          {showArrivalComplement && (
            <Input
              className="mt-2"
              placeholder="Complément d'adresse"
            />
          )}
        </div>

        {/* Distance Indicator */}
        {(distanceInfo.isCalculating || distanceInfo.distance_km) && (
          <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 animate-in fade-in duration-300">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-full shadow-sm">
                <Navigation className="w-4 h-4 text-[#1db584]" />
              </div>
              <div className="flex-1">
                {distanceInfo.isCalculating ? (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Calcul de la distance...</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-[#1db584]">
                        {distanceInfo.distance_km?.toLocaleString('fr-FR')}
                      </span>
                      <span className="text-sm text-gray-600">km</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {distanceInfo.segments > 1 
                        ? `Distance totale (${distanceInfo.segments} segments)`
                        : 'Distance estimée du trajet'
                      }
                    </span>
                    {(formData.stops || []).length > 0 && (
                      <div className="mt-1 flex items-center gap-1">
                        <Route className="w-3 h-3 text-[#1db584]" />
                        <span className="text-xs text-[#1db584] font-medium">
                          {(formData.stops || []).length} étape{(formData.stops || []).length > 1 ? 's' : ''} intermédiaire{(formData.stops || []).length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <MapPin className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
        )}
      </div>

      {/* ✨ Section Aller-retour (23/04/2026) */}
      <RoundTripSection
        formData={formData}
        setFormData={setFormData}
        merchandiseTypes={merchandiseTypes}
        containerTypes={containerTypes}
      />
      {/* Delivery Date */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#1db584]" />
          <Label>Date de livraison *</Label>
        </div>
        <Input
          type="date"
          value={formData.deliveryDate}
          onChange={(e) => handleChange('deliveryDate', e.target.value)}
          className="focus:border-[#1db584] focus:ring-[#1db584]"
          required
        />
      </div>

      {/* Vehicle Type */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900">Choisir un véhicule *</h3>
        <VehicleSelector
          vehicles={vehicleTypes}
          selected={formData.vehicleType}
          onSelect={(value) => handleChange('vehicleType', value)}
        />
        
        {/* Champ "Autre" pour véhicule - avec recherche */}
        {formData.vehicleType === 'autre' && (
          <div className="mt-3 animate-in slide-in-from-top-2 duration-300">
            <Label className="text-sm text-gray-600 mb-2 block">
              Recherchez ou saisissez le type de véhicule
            </Label>
            <VehicleSearchInput
              value={formData.vehicleTypeOther}
              onChange={(value) => handleChange('vehicleTypeOther', value)}
              placeholder="Rechercher un type de véhicule..."
            />
          </div>
        )}
      </div>

      {/* Operation Type */}
      <div className="space-y-2">
        <Label>Nature de l&apos;opération *</Label>
        <Select value={formData.operationType || undefined} onValueChange={(value) => handleChange('operationType', value)}>
          <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584]">
            <SelectValue placeholder="-- Sélectionnez --" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            {operationTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Champ "Autre" pour opération */}
        {formData.operationType === 'operation_speciale' && (
          <div className="mt-2 animate-in slide-in-from-top-2 duration-300">
            <Input
              placeholder="Précisez la nature de l'opération..."
              value={formData.operationTypeOther}
              onChange={(e) => handleChange('operationTypeOther', e.target.value)}
              className="focus:border-[#1db584] focus:ring-[#1db584]"
            />
          </div>
        )}
      </div>

      {/* Container Type (shown only when container transport is selected) */}
      {showContainerOptions && (
        <div className="space-y-3 animate-in slide-in-from-top-2 duration-300 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1db584]" />
            <Label className="text-base font-semibold">Type de conteneur *</Label>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Sélectionnez le type de conteneur à transporter
          </p>
          <Select 
            value={formData.containerType || undefined} 
            onValueChange={(value) => handleChange('containerType', value)}
          >
            <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584] bg-white h-auto min-h-[44px] py-2" data-testid="container-type-trigger">
              <SelectValue placeholder="-- Choisissez un type de conteneur --" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px] overflow-y-auto">
              {containerTypes.map((type) => (
                <SelectItem key={type.value} value={type.value} className="py-3">
                  <div className="flex items-start gap-2">
                    <span className="text-lg leading-none mt-0.5">{type.icon}</span>
                    <div className="min-w-0">
                      <div className="font-medium leading-tight">{type.label}</div>
                      <div className="text-xs text-gray-500 leading-snug">
                        {type.dimensions}{type.description ? ` · ${type.description}` : ''}
                      </div>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 p-3 bg-blue-100 rounded-md">
            <p className="text-xs text-blue-800 flex items-start gap-2">
              <span className="text-base">ℹ️</span>
              <span>
                Les conteneurs sont des unités standardisées pour le transport maritime et routier. 
                Sélectionnez le type adapté à votre marchandise (standard, frigorifique, open-top, etc.)
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Merchandise Type (hidden when container transport is selected) */}
      {!showContainerOptions && (
        <div className="space-y-2">
          <Label>Type de marchandise *</Label>
          <Select 
            value={formData.merchandiseType || undefined} 
            onValueChange={(value) => handleChange('merchandiseType', value)}
          >
            <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584]">
              <SelectValue placeholder="-- Sélectionnez --" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              {merchandiseTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Champ "Autre" pour marchandise */}
          {formData.merchandiseType === 'autre' && (
            <div className="mt-2 animate-in slide-in-from-top-2 duration-300">
              <Input
                placeholder="Précisez le type de marchandise..."
                value={formData.merchandiseTypeOther}
                onChange={(e) => handleChange('merchandiseTypeOther', e.target.value)}
                className="focus:border-[#1db584] focus:ring-[#1db584]"
              />
            </div>
          )}
        </div>
      )}

      {/* Weight */}
      <div className="space-y-2">
        <Label>Poids approximatif</Label>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => handleWeightChange(-1)}
            className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <div className="flex-1 flex items-center justify-center gap-2">
            <input
              type="number"
              min="1"
              max="220"
              value={formData.weight}
              onChange={(e) => handleWeightInput(e.target.value)}
              onBlur={handleWeightBlur}
              className="w-20 text-2xl font-semibold text-[#1db584] text-center border-b-2 border-gray-200 focus:border-[#1db584] focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-gray-600">tonne{formData.weight > 1 ? 's' : ''}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => handleWeightChange(1)}
            className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-gray-500 text-center">De 1 à 220 tonnes</p>
      </div>

      {/* Number of Trucks */}
      <div className="space-y-2">
        <Label>Nombre de camions</Label>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => handleChange('numberOfTrucks', Math.max(1, (formData.numberOfTrucks || 1) - 1))}
            className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <div className="flex-1 flex items-center justify-center gap-2">
            <input
              type="number"
              min="1"
              max="50"
              value={formData.numberOfTrucks || 1}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                if (val === '' || (!isNaN(val) && val >= 1 && val <= 50)) {
                  handleChange('numberOfTrucks', val === '' ? 1 : val);
                }
              }}
              className="w-16 text-2xl font-semibold text-[#1db584] text-center border-b-2 border-gray-200 focus:border-[#1db584] focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-gray-600">camion{(formData.numberOfTrucks || 1) > 1 ? 's' : ''}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => handleChange('numberOfTrucks', Math.min(50, (formData.numberOfTrucks || 1) + 1))}
            className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-gray-500 text-center">De 1 à 50 camions</p>
      </div>

      {/* Insurance Section */}
      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-[#1db584]" />
          <h3 className="font-semibold text-gray-900">Protection & Assurance</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="needsInsurance"
              checked={formData.needsInsurance}
              onChange={(e) => handleChange('needsInsurance', e.target.checked)}
              className="mt-1 h-4 w-4 text-[#1db584] border-gray-300 rounded focus:ring-[#1db584]"
            />
            <label htmlFor="needsInsurance" className="text-sm text-gray-700 cursor-pointer">
              <span className="font-medium">Souscrire à une assurance marchandise</span>
              <p className="text-xs text-gray-500 mt-1">Protection renforcée contre les dommages et pertes durant le transport</p>
            </label>
          </div>

          {formData.needsInsurance && (
            <div className="ml-7 space-y-3 animate-in slide-in-from-top-2 duration-300">
              <div>
                <Label className="text-sm">Valeur déclarée de la marchandise (XOF)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 5000000"
                  value={formData.merchandiseValue}
                  onChange={(e) => handleChange('merchandiseValue', e.target.value)}
                  className="focus:border-[#1db584] focus:ring-[#1db584] mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Cette valeur servira de base pour le calcul de l&apos;assurance</p>
              </div>
            </div>
          )}

          {extRefConfig.enabled && (
            <div data-testid="external-ref-form-block">
              <Label className="text-sm">
                {extRefConfig.label}
                {extRefConfig.required ? ' *' : ' (optionnel)'}
              </Label>
              <Input
                value={formData.externalRef}
                maxLength={50}
                placeholder={extRefConfig.helpText || extRefConfig.label}
                onChange={(e) => handleChange('externalRef', e.target.value)}
                className="focus:border-[#1db584] focus:ring-[#1db584] mt-1"
                disabled={!extRefEditable}
                data-testid="external-ref-form-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {extRefEditable
                  ? (extRefConfig.helpText || 'Votre référence interne, reprise sur vos documents et vos exports.')
                  : 'Cette référence est renseignée par nos équipes.'}
              </p>
            </div>
          )}

          <div>
            <Label className="text-sm">Instructions spéciales (optionnel)</Label>            <textarea
              placeholder="Ex: Marchandise fragile, nécessite une manutention délicate..."
              value={formData.specialInstructions}
              onChange={(e) => handleChange('specialInstructions', e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:border-[#1db584] focus:ring-[#1db584] focus:outline-none resize-none"
              rows="3"
            />
          </div>
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full bg-gradient-to-r from-[#1db584] to-[#16a075] hover:from-[#16a075] hover:to-[#128f69] text-white py-6 text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Envoi en cours...
          </>
        ) : (
          'Demander un transport'
        )}
      </Button>

      {/* Success Modal */}
      <OrderSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        pricing={{
          total: createdOperation?.estimatedPrice || 0
        }}
        operationId={createdOperation?.id}
      />
    </form>
  );
};

export default TransportForm;
